import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { BoundaryHeader, UnitCell } from "./observabilityCells";
import type { TableHotspotTableRow } from "./TableHotspotTableRow";
import styles from "./ObservabilityPage.module.css";

/**
 * 테이블 hotspot 열이다. **읽기** · **크기/인덱스** · **쓰기 전파** 세 묶음을 경계선으로 구분한다.
 *
 * 한 칸은 큰 수치와 보조 한 줄이다.
 * - 창 요약 칸의 보조는 봉우리·산포다. 이 값으로 "항상 큰 테이블" 과 "한 번 크게 튄 테이블" 을
 *   구분한다.
 * - 창 합 칸의 보조는 그 합의 세부 수치다. 예: seq scan 횟수 아래 읽은 행 수. 횟수만 보면
 *   5만 행 full scan 과 1행 scan 이 같은 1 이다.
 *
 * 모든 열에서 머리글 정렬을 쓸 수 있다. 서버는 크기 상위 20개를 블록 접근 순으로 주고, 그 20개가
 * 대상 전체다. 그래서 정렬은 볼 테이블을 바꾸지 않고, 어느 값으로 정렬할지만 바꾼다.
 */
export function createTableHotspotColumns(): readonly DataTableColumn<TableHotspotTableRow>[] {
  return [
    {
      key: "qualifiedName",
      header: "테이블",
      width: "minmax(240px, 1fr)",
      sortable: "both",
      render: (row) => <NameCell row={row} />,
    },
    {
      key: "blockAccess",
      header: "블록 접근",
      width: "minmax(200px, 240px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.blockAccess} />,
    },
    {
      key: "seqScan",
      header: "seq scan",
      width: "minmax(140px, 170px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.seqScan} />,
    },
    {
      key: "indexScan",
      header: "index scan",
      width: "minmax(150px, 180px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.indexScan} />,
    },
    {
      // 읽기 묶음이 여기서 끝나고 크기·인덱스 묶음이 시작된다. 이 묶음의 첫 열이 경계선을 가진다.
      key: "heapBytes",
      header: <BoundaryHeader label="heap" />,
      width: "minmax(190px, 220px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.heapBytes} boundary />,
    },
    {
      key: "indexBytes",
      header: "index",
      width: "minmax(190px, 220px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.indexBytes} />,
    },
    {
      key: "unusedIndexes",
      header: "미사용 인덱스",
      width: "minmax(160px, 200px)",
      sortable: "both",
      render: (row) => <UnusedIndexCell row={row} />,
    },
    {
      // 쓰기 전파 묶음의 시작이다.
      key: "rowWrites",
      header: <BoundaryHeader label="행 쓰기" />,
      width: "minmax(240px, 290px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.rowWrites} boundary />,
    },
    {
      key: "propagatedUpdates",
      header: "인덱스 전파 갱신",
      width: "minmax(150px, 180px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.propagatedUpdates} />,
    },
    {
      key: "statsLag",
      header: "통계 지연",
      width: "minmax(200px, 240px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.statsLag} />,
    },
    {
      key: "lastAnalyzeAt",
      header: "마지막 analyze",
      width: "170px",
      align: "right",
      sortable: "both",
      render: (row) => <span className={styles.numeric}>{row.lastAnalyzeAt}</span>,
    },
  ];
}

/** 테이블 이름 아래에 행 수를 보조로 표시한다. 증분 규모를 판단하는 맥락이다. */
function NameCell({ row }: { row: TableHotspotTableRow }): ReactNode {
  return (
    <span className={styles.unitCell} title={row.qualifiedName}>
      <span className={styles.fingerprint}>{row.qualifiedName}</span>
      {row.rowCount === "" ? null : <span className={styles.podMetricHint}>{row.rowCount}</span>}
    </span>
  );
}

/**
 * 미사용 인덱스 칸이다. 개수와 크기를 표시하고, 이름 목록은 툴팁에 표시한다.
 *
 * 이름이 화면에 있어야 한다. 개수만으로는 무엇을 지울지 정할 수 없기 때문이다. 이름을 칸에 모두
 * 쓰지 않는다. 이름이 길어서 열 폭을 모두 차지하기 때문이다.
 */
function UnusedIndexCell({ row }: { row: TableHotspotTableRow }): ReactNode {
  const cell = <UnitCell view={row.unusedIndexes} />;
  if (row.unusedIndexNames.length === 0) {
    return cell;
  }
  return (
    <HatTextTooltip
      text={`통계 리셋 이후 한 번도 안 쓰인 인덱스(PK·unique 제외): ${row.unusedIndexNames.join(", ")}`}
    >
      {cell}
    </HatTextTooltip>
  );
}
